<template>
  <div class="abs-page">
    <div class="abs-header">
      <h1 class="abs-title">店家管理</h1>
      <div class="abs-header-actions">
        <button class="abs-btn abs-btn--copy" @click="copyStoreApplyLink">{{ copiedApply ? '已複製！' : '複製店家申請連結' }}</button>
        <button class="abs-btn abs-btn--copy" @click="copyStoreLoginLink">{{ copiedLogin ? '已複製！' : '複製店家登入連結' }}</button>
        <button class="abs-btn abs-btn--primary" @click="openCreate">＋ 新增店家</button>
      </div>
    </div>

    <!-- 篩選列 -->
    <div class="abs-filters">
      <select v-model="filterRegion" class="abs-input abs-input--select" @change="applyFilter">
        <option value="">全部地區</option>
        <option v-for="r in regions" :key="r.id" :value="r.id">{{ r.name }}</option>
      </select>
      <select v-model="filterStatus" class="abs-input abs-input--select" @change="applyFilter">
        <option value="">全部狀態</option>
        <option value="pending">待審核</option>
        <option value="active">上線</option>
        <option value="inactive">下架</option>
      </select>
      <input
        v-model="filterKeyword"
        class="abs-input abs-input--search"
        placeholder="搜尋店名…"
        @input="applyFilter"
      />
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="abs-state">
      <span class="abs-spinner abs-spinner--lg" /> 載入中…
    </div>

    <!-- 錯誤 -->
    <div v-else-if="loadError" class="abs-alert abs-alert--error">{{ loadError }}</div>

    <!-- 空 -->
    <div v-else-if="filtered.length === 0" class="abs-empty">目前沒有符合條件的店家</div>

    <!-- 店家列表 -->
    <div v-else class="abs-list">
      <div
        v-for="store in filtered"
        :key="store.id"
        class="abs-card"
        :class="{ 'abs-card--inactive': store.status === 'inactive' }"
      >
        <!-- 封面圖 -->
        <div class="abs-card__thumb">
          <img
            v-if="store.cover_image && !imgErrors.has(store.id)"
            :src="resolveImg(store.cover_image)"
            :alt="store.name"
            class="abs-card__img"
            @error="imgErrors.add(store.id)"
          />
          <div v-else class="abs-card__no-img">無圖</div>
        </div>

        <!-- 資訊 -->
        <div class="abs-card__body">
          <div class="abs-card__top">
            <span class="abs-store-name">{{ store.name }}</span>
            <span v-if="store.is_featured" class="abs-featured-badge">⭐ 推薦</span>
            <span class="abs-status-tag" :class="`abs-status-tag--${store.status}`">
              {{ { active: '上線', inactive: '下架', pending: '待審核' }[store.status] ?? store.status }}
            </span>
          </div>
          <div class="abs-card__meta">
            <span class="abs-region-tag">{{ store.region_name }}</span>
            <span v-if="store.phone">{{ store.phone }}</span>
            <span v-if="store.email" class="abs-email">{{ store.email }}</span>
          </div>
          <div v-if="store.category_ids && store.category_ids.length" class="abs-cat-tags">
            <span
              v-for="cid in store.category_ids"
              :key="cid"
              class="abs-cat-tag"
            >{{ categoryName(cid) }}</span>
          </div>
          <div v-if="store.address" class="abs-card__addr">{{ store.address }}</div>
          <div v-if="store.description" class="abs-card__desc">{{ store.description }}</div>
        </div>

        <!-- 操作 -->
        <div class="abs-card__actions">
          <!-- 待審核：顯示審核通過 / 拒絕 -->
          <template v-if="store.status === 'pending'">
            <button
              class="abs-btn abs-btn--sm abs-btn--success"
              :disabled="approving[store.id]"
              @click="doApprove(store)"
            >
              <span v-if="approving[store.id]" class="abs-spinner abs-spinner--sm abs-spinner--white" />
              審核通過
            </button>
            <button
              class="abs-btn abs-btn--sm abs-btn--danger"
              :disabled="rejecting[store.id]"
              @click="confirmReject(store)"
            >
              拒絕
            </button>
          </template>

          <!-- 已上線 / 下架：正常操作 -->
          <template v-else>
            <button
              class="abs-btn abs-btn--sm"
              :class="store.is_featured ? 'abs-btn--featured-on' : 'abs-btn--featured-off'"
              :disabled="featuring[store.id]"
              :title="store.is_featured ? '取消推薦' : '設為推薦'"
              @click="toggleFeatured(store)"
            >
              {{ store.is_featured ? '⭐ 推薦中' : '☆ 設推薦' }}
            </button>
            <button
              v-if="store.login_token"
              class="abs-btn abs-btn--sm abs-btn--link"
              @click="copyLoginUrl(store)"
              :title="`複製 ${store.name} 的專屬登入連結`"
            >
              複製登入連結
            </button>
            <button class="abs-btn abs-btn--sm abs-btn--secondary" @click="openEdit(store)">
              編輯
            </button>
            <button class="abs-btn abs-btn--sm abs-btn--info" @click="goServices(store)">
              服務
            </button>
            <button
              v-if="store.status === 'active'"
              class="abs-btn abs-btn--sm abs-btn--warn"
              @click="confirmDeactivate(store)"
            >
              下架
            </button>
            <button
              v-else
              class="abs-btn abs-btn--sm abs-btn--success"
              :disabled="reactivating[store.id]"
              @click="reactivate(store)"
            >
              <span v-if="reactivating[store.id]" class="abs-spinner abs-spinner--sm abs-spinner--white" />
              重新上線
            </button>
            <button class="abs-btn abs-btn--sm abs-btn--danger" @click="confirmHardDelete(store)">
              刪除
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- ── 新增 / 編輯 Modal ──────────────────────── -->
    <teleport to="body">
      <div v-if="showModal" class="abs-modal-overlay" @click.self="closeModal">
        <div class="abs-modal" role="dialog">
          <div class="abs-modal__header">
            <h2 class="abs-modal__title">{{ isEdit ? '編輯店家' : '新增店家' }}</h2>
            <button class="abs-modal__close" @click="closeModal">✕</button>
          </div>

          <div class="abs-modal__body">

            <!-- ── 基本資料 ── -->
            <p class="abs-section-label">基本資料</p>

            <!-- 地區 -->
            <div class="abs-field">
              <label>地區 <span class="abs-req">*</span></label>
              <select
                v-model="form.region_id"
                class="abs-input"
                :class="{ 'abs-input--error': errors.region_id }"
              >
                <option value="">請選擇地區</option>
                <option v-for="r in regions" :key="r.id" :value="r.id">{{ r.name }}</option>
              </select>
              <p v-if="errors.region_id" class="abs-field-err">{{ errors.region_id }}</p>
            </div>

            <!-- 店名 -->
            <div class="abs-field">
              <label>店名 <span class="abs-req">*</span></label>
              <input
                v-model="form.name"
                class="abs-input"
                :class="{ 'abs-input--error': errors.name }"
                placeholder="請輸入店名"
              />
              <p v-if="errors.name" class="abs-field-err">{{ errors.name }}</p>
            </div>

            <!-- 地址 -->
            <div class="abs-field">
              <label>地址 <span class="abs-required">*</span></label>
              <input
                v-model="form.address"
                class="abs-input"
                :class="{ 'abs-input--error': errors.address }"
                placeholder="請輸入地址"
              />
              <p v-if="errors.address" class="abs-field-err">{{ errors.address }}</p>
            </div>

            <!-- 電話 + 通知 Email -->
            <div class="abs-field-row">
              <div class="abs-field abs-field--no-mb">
                <label>電話 <span class="abs-required">*</span></label>
                <input
                  v-model="form.phone"
                  class="abs-input"
                  :class="{ 'abs-input--error': errors.phone }"
                  placeholder="請輸入電話"
                />
                <p v-if="errors.phone" class="abs-field-err">{{ errors.phone }}</p>
              </div>
              <div class="abs-field abs-field--no-mb">
                <label>通知 Email <span class="abs-required">*</span></label>
                <input
                  v-model="form.email"
                  type="email"
                  class="abs-input"
                  :class="{ 'abs-input--error': errors.email }"
                  placeholder="接收新預約通知"
                />
                <p v-if="errors.email" class="abs-field-err">{{ errors.email }}</p>
              </div>
            </div>

            <!-- 封面圖 -->
            <div class="abs-field">
              <label>封面圖</label>

              <!-- 上傳 / URL 並排 -->
              <div class="abs-img-actions">
                <label class="abs-btn abs-btn--secondary abs-btn--sm abs-img-upload-btn">
                  <input
                    type="file"
                    accept="image/*"
                    style="display:none"
                    @change="onImageFileChange"
                  />
                  📷 選擇圖片上傳
                </label>
                <span class="abs-img-or">或</span>
                <input
                  v-model="form.cover_image"
                  class="abs-input abs-img-url-input"
                  placeholder="貼上圖片網址 https://…"
                  @input="imgInfo = null; previewError = false"
                />
              </div>
              <p class="abs-img-hint">上傳限制：JPG / PNG / WEBP，單檔不超過 8MB；系統自動壓縮輸出至 400KB 以內</p>

              <!-- 上傳中 -->
              <div v-if="imageUploading" class="abs-img-uploading">
                <span class="abs-spinner abs-spinner--sm" /> 上傳中…
              </div>

              <!-- 3:4 直式預覽（跟前台一樣） -->
              <template v-if="form.cover_image && !imageUploading">
                <div v-if="previewError" class="abs-img-err">⚠ 無法載入此圖片網址</div>
                <div v-else class="abs-preview-wrap">
                  <div class="abs-preview-frame">
                    <img
                      :src="resolveImg(form.cover_image)"
                      alt="封面預覽"
                      class="abs-preview-img"
                      @load="onPreviewLoad"
                      @error="previewError = true"
                    />
                    <div v-if="imgInfo" class="abs-preview-badge">
                      {{ imgInfo.width }} × {{ imgInfo.height }} px
                      <span v-if="imgInfo.width < 600 || imgInfo.height < 600" class="abs-preview-warn">
                        ⚠ 建議至少 600 × 600
                      </span>
                    </div>
                  </div>
                  <p class="abs-preview-hint">↑ 前台顯示效果（IG 正方形 1:1）</p>
                </div>
              </template>
            </div>

            <!-- 簡介 -->
            <div class="abs-field">
              <label>簡介</label>
              <textarea
                v-model="form.description"
                class="abs-input abs-textarea"
                rows="3"
                placeholder="選填"
              />
            </div>

            <!-- Google Maps 連結 -->
            <div class="abs-field">
              <label>Google Maps 導航連結</label>
              <input
                v-model="form.map_url"
                class="abs-input"
                placeholder="https://maps.app.goo.gl/… 或完整 Google Maps 網址"
              />
              <p class="abs-field-hint">Google Maps 找到店家 → 分享 → 複製連結</p>
            </div>

            <!-- 服務分類（多選）-->
            <div class="abs-field">
              <label>服務分類</label>
              <div class="abs-checkbox-group">
                <label
                  v-for="c in categories"
                  :key="c.id"
                  class="abs-checkbox"
                >
                  <input
                    type="checkbox"
                    :value="c.id"
                    v-model="form.category_ids"
                  />
                  <span>{{ c.name }}</span>
                </label>
              </div>
            </div>

            <!-- 狀態（僅編輯時顯示） -->
            <div v-if="isEdit" class="abs-field">
              <label>狀態</label>
              <div class="abs-radio-group">
                <label class="abs-radio">
                  <input type="radio" v-model="form.status" value="active" />
                  <span>上線</span>
                </label>
                <label class="abs-radio">
                  <input type="radio" v-model="form.status" value="inactive" />
                  <span>下架</span>
                </label>
              </div>
            </div>

            <!-- ── 後台帳號（僅新增時顯示）── -->
            <template v-if="!isEdit">
              <div class="abs-divider" />
              <p class="abs-section-label">
                後台管理員帳號
                <span class="abs-section-hint">建立後可用此帳號登入店家後台</span>
              </p>

              <div class="abs-field-row">
                <!-- 帳號 -->
                <div class="abs-field abs-field--no-mb">
                  <label>登入帳號 <span class="abs-req">*</span></label>
                  <input
                    v-model="form.account"
                    class="abs-input"
                    :class="{ 'abs-input--error': errors.account }"
                    placeholder="英數字，建議 6～20 碼"
                    autocomplete="off"
                  />
                  <p v-if="errors.account" class="abs-field-err">{{ errors.account }}</p>
                </div>
                <!-- 初始密碼 -->
                <div class="abs-field abs-field--no-mb">
                  <label>初始密碼 <span class="abs-req">*</span></label>
                  <input
                    v-model="form.password"
                    type="password"
                    class="abs-input"
                    :class="{ 'abs-input--error': errors.password }"
                    placeholder="至少 6 碼"
                    autocomplete="new-password"
                  />
                  <p v-if="errors.password" class="abs-field-err">{{ errors.password }}</p>
                </div>
              </div>
            </template>

            <!-- API 錯誤 -->
            <p v-if="apiError" class="abs-api-error">{{ apiError }}</p>
          </div>

          <div class="abs-modal__footer">
            <button class="abs-btn abs-btn--secondary" @click="closeModal">取消</button>
            <button
              class="abs-btn abs-btn--primary"
              :disabled="submitting"
              @click="submit"
            >
              <span v-if="submitting" class="abs-spinner abs-spinner--sm abs-spinner--white" />
              {{ submitting ? '儲存中…' : (isEdit ? '儲存變更' : '新增店家') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── 拒絕申請確認 Modal ───────────────────────── -->
    <teleport to="body">
      <div v-if="showRejectConfirm" class="abs-modal-overlay" @click.self="showRejectConfirm = false">
        <div class="abs-modal abs-modal--sm" role="dialog">
          <div class="abs-modal__header">
            <h2 class="abs-modal__title">確認拒絕申請</h2>
            <button class="abs-modal__close" @click="showRejectConfirm = false">✕</button>
          </div>
          <div class="abs-modal__body">
            <p style="margin:0">
              確定拒絕「<strong>{{ rejectTarget?.name }}</strong>」的入駐申請嗎？
            </p>
            <p class="abs-modal__hint">店家資料與帳號將一併刪除，此操作無法復原。</p>
            <p v-if="rejectError" class="abs-api-error">{{ rejectError }}</p>
          </div>
          <div class="abs-modal__footer">
            <button class="abs-btn abs-btn--secondary" @click="showRejectConfirm = false">取消</button>
            <button
              class="abs-btn abs-btn--danger"
              :disabled="rejectLoading"
              @click="doReject"
            >
              <span v-if="rejectLoading" class="abs-spinner abs-spinner--sm abs-spinner--white" />
              {{ rejectLoading ? '處理中…' : '確認拒絕' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── 刪除確認 Modal ─────────────────────────── -->
    <teleport to="body">
      <div v-if="showDeleteConfirm" class="abs-modal-overlay" @click.self="showDeleteConfirm = false">
        <div class="abs-modal abs-modal--sm" role="dialog">
          <div class="abs-modal__header">
            <h2 class="abs-modal__title">確認刪除店家</h2>
            <button class="abs-modal__close" @click="showDeleteConfirm = false">✕</button>
          </div>
          <div class="abs-modal__body">
            <p style="margin:0">
              確定刪除「<strong>{{ deleteTarget?.name }}</strong>」嗎？
            </p>
            <p class="abs-modal__hint">店家資料、帳號、服務、預約記錄將全部刪除，此操作無法復原。</p>
            <p v-if="deleteError" class="abs-api-error">{{ deleteError }}</p>
          </div>
          <div class="abs-modal__footer">
            <button class="abs-btn abs-btn--secondary" @click="showDeleteConfirm = false">取消</button>
            <button
              class="abs-btn abs-btn--danger"
              :disabled="deleteLoading"
              @click="doHardDelete"
            >
              <span v-if="deleteLoading" class="abs-spinner abs-spinner--sm abs-spinner--white" />
              {{ deleteLoading ? '刪除中…' : '確認刪除' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── 下架確認 Modal ─────────────────────────── -->
    <teleport to="body">
      <div v-if="showConfirm" class="abs-modal-overlay" @click.self="showConfirm = false">
        <div class="abs-modal abs-modal--sm" role="dialog">
          <div class="abs-modal__header">
            <h2 class="abs-modal__title">確認下架</h2>
            <button class="abs-modal__close" @click="showConfirm = false">✕</button>
          </div>
          <div class="abs-modal__body">
            <p style="margin:0">
              確定要將「<strong>{{ confirmTarget?.name }}</strong>」設為下架嗎？
            </p>
            <p class="abs-modal__hint">下架後前台不顯示此店家，但預約記錄保留。</p>
            <p v-if="confirmError" class="abs-api-error">{{ confirmError }}</p>
          </div>
          <div class="abs-modal__footer">
            <button class="abs-btn abs-btn--secondary" @click="showConfirm = false">取消</button>
            <button
              class="abs-btn abs-btn--danger"
              :disabled="confirmLoading"
              @click="doDeactivate"
            >
              <span v-if="confirmLoading"
                class="abs-spinner abs-spinner--sm abs-spinner--white" />
              {{ confirmLoading ? '處理中…' : '確認下架' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()

// ── 401 統一處理：導回平台管理員登入 ─────────────────
function handle401() {
  router.push({ name: 'AdminLogin' })
}

// 圖片 URL 正規化（本機開發用）
function resolveImg(url) {
  if (!url) return ''
  // 相對路徑 /api/booking/img/... → 本機開發時加上正式機網域
  if (url.startsWith('/api/booking/img/') && process.env.NODE_ENV === 'development') {
    return `https://jiliskin.com${url}`
  }
  // 本機開發：http://127.0.0.1:PORT/uploads/... → /uploads/...（走 proxy）
  return url.replace(/^http:\/\/127\.0\.0\.1:\d+\/uploads\//, '/uploads/')
}

function goServices(store) {
  router.push({ name: 'AdminBookingServices', query: { storeId: store.id } })
}

function copyLoginUrl(store) {
  const url = `${window.location.origin}/store-admin/login?code=${store.login_token}`
  navigator.clipboard.writeText(url).then(() => {
    alert(`已複製「${store.name}」的登入連結：\n${url}`)
  }).catch(() => {
    prompt('請手動複製此連結：', url)
  })
}

// ── 資料 ─────────────────────────────────────────────
const stores     = ref([])
const regions    = ref([])
const categories = ref([])
const loading    = ref(false)
const loadError  = ref('')
const copiedApply = ref(false)
const copiedLogin = ref(false)
const imgErrors  = reactive(new Set())

function categoryName(id) {
  return categories.value.find(c => c.id === id)?.name ?? `#${id}`
}

// ── 篩選 ─────────────────────────────────────────────
const filterRegion  = ref('')
const filterStatus  = ref('')
const filterKeyword = ref('')

const filtered = computed(() =>
  stores.value.filter(s => {
    if (filterRegion.value  && s.region_id !== Number(filterRegion.value)) return false
    if (filterStatus.value  && s.status    !== filterStatus.value)         return false
    if (filterKeyword.value && !s.name.includes(filterKeyword.value))      return false
    return true
  })
)

function applyFilter() {} // computed 自動響應，此函式供 @change/@input 掛用

// ── Modal 狀態 ────────────────────────────────────────
const showModal  = ref(false)
const isEdit     = ref(false)
const submitting = ref(false)
const apiError   = ref('')

const emptyForm = () => ({
  region_id:    '',
  name:         '',
  address:      '',
  phone:        '',
  email:        '',
  cover_image:  '',
  description:  '',
  map_url:      '',
  status:       'active',
  category_ids: [],
  // 僅新增時使用
  account:      '',
  password:     '',
})
const form   = ref(emptyForm())
const errors = ref({})
let editId   = null

// ── 封面圖上傳 ────────────────────────────────────────
const imageUploading = ref(false)
const imgInfo        = ref(null)   // { width, height }
const previewError   = ref(false)

async function onImageFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) return
  imageUploading.value = true
  imgInfo.value        = null
  previewError.value   = false
  try {
    const fd = new FormData()
    fd.append('files', file)
    fd.append('folder', 'stores')
    const res = await axios.post('/api/admin/images/upload', fd)
    if (res.data.success && res.data.items?.[0]) {
      const item = res.data.items[0]
      form.value.cover_image = item.url
      if (item.width && item.height) {
        imgInfo.value = { width: item.width, height: item.height }
      }
    }
  } catch (err) {
    apiError.value = err.response?.data?.message || '圖片上傳失敗'
  } finally {
    imageUploading.value = false
    e.target.value = ''   // 重置 file input，允許重複選同一張
  }
}

function onPreviewLoad(e) {
  if (!imgInfo.value) {
    imgInfo.value = { width: e.target.naturalWidth, height: e.target.naturalHeight }
  }
}

// ── 下架確認 ──────────────────────────────────────────
const showConfirm    = ref(false)
const confirmTarget  = ref(null)
const confirmLoading = ref(false)
const confirmError   = ref('')

// 推薦切換 loading 狀態
const featuring = reactive({})

// ── 切換推薦 ──────────────────────────────────────────
async function toggleFeatured(store) {
  featuring[store.id] = true
  try {
    const res = await axios.post(`/api/admin/booking/stores/${store.id}/toggle-featured`)
    if (res.data.success) {
      store.is_featured = res.data.is_featured
    }
  } catch (e) {
    alert(e.response?.data?.message || '操作失敗')
  } finally {
    featuring[store.id] = false
  }
}

// 重新上線 loading 狀態（逐筆）
const reactivating = reactive({})

// ── 審核通過 ──────────────────────────────────────────
const approving = reactive({})

async function doApprove(store) {
  approving[store.id] = true
  try {
    await axios.post(`/api/admin/booking/stores/${store.id}/approve`)
    store.status = 'active'
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    alert(e.response?.data?.message || '操作失敗，請稍後再試')
  } finally {
    approving[store.id] = false
  }
}

// ── 拒絕申請 ──────────────────────────────────────────
const rejecting          = reactive({})
const showRejectConfirm  = ref(false)
const rejectTarget       = ref(null)
const rejectLoading      = ref(false)
const rejectError        = ref('')

function confirmReject(store) {
  rejectTarget.value  = store
  rejectError.value   = ''
  showRejectConfirm.value = true
}

async function doReject() {
  rejectLoading.value = true
  rejectError.value   = ''
  try {
    await axios.post(`/api/admin/booking/stores/${rejectTarget.value.id}/reject`)
    stores.value = stores.value.filter(s => s.id !== rejectTarget.value.id)
    showRejectConfirm.value = false
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    rejectError.value = e.response?.data?.message || '操作失敗，請稍後再試'
  } finally {
    rejectLoading.value = false
  }
}

// ── 載入 ──────────────────────────────────────────────
async function loadAll() {
  loading.value   = true
  loadError.value = ''
  try {
    const [sRes, rRes, cRes] = await Promise.all([
      axios.get('/api/admin/booking/stores'),
      axios.get('/api/admin/booking/regions'),
      axios.get('/api/admin/booking/categories'),
    ])
    stores.value     = sRes.data.stores     || []
    regions.value    = rRes.data.regions    || []
    categories.value = cRes.data.categories || []
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    loadError.value = e.response?.data?.message || '載入失敗，請重新整理'
  } finally {
    loading.value = false
  }
}

// ── 開啟 Modal ────────────────────────────────────────
function openCreate() {
  isEdit.value    = false
  editId          = null
  form.value      = emptyForm()
  errors.value    = {}
  apiError.value  = ''
  imgInfo.value   = null
  previewError.value = false
  showModal.value = true
}

function openEdit(store) {
  isEdit.value = true
  editId       = store.id
  form.value   = {
    region_id:    store.region_id    ?? '',
    name:         store.name         ?? '',
    address:      store.address      ?? '',
    phone:        store.phone        ?? '',
    email:        store.email        ?? '',
    cover_image:  store.cover_image  ?? '',
    description:  store.description  ?? '',
    map_url:      store.map_url      ?? '',
    status:       store.status       ?? 'active',
    category_ids: store.category_ids ? [...store.category_ids] : [],
    account:      '',   // 編輯不顯示，填空即可
    password:     '',
  }
  errors.value       = {}
  apiError.value     = ''
  imgInfo.value      = null
  previewError.value = false
  showModal.value    = true
}

function closeModal() { showModal.value = false }

// ── 驗證 ──────────────────────────────────────────────
function validate() {
  const e = {}
  if (!form.value.region_id)       e.region_id = '請選擇地區'
  if (!form.value.name.trim())     e.name      = '請輸入店名'
  if (!form.value.address.trim())  e.address   = '請輸入地址'
  if (!form.value.phone.trim())    e.phone     = '請輸入電話'
  if (!form.value.email.trim()) {
    e.email = '請輸入通知 Email'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    e.email = 'Email 格式不正確'
  }
  // 新增時才驗證帳號密碼
  if (!isEdit.value) {
    if (!form.value.account.trim())   e.account  = '請填寫後台登入帳號'
    if (form.value.password.length < 6) e.password = '密碼至少 6 碼'
  }
  errors.value = e
  return Object.keys(e).length === 0
}

// ── 送出 ──────────────────────────────────────────────
async function submit() {
  if (!validate()) return
  submitting.value = true
  apiError.value   = ''
  try {
    if (isEdit.value) {
      // 編輯：PUT，不傳 account / password
      const payload = {
        region_id:    Number(form.value.region_id),
        name:         form.value.name.trim(),
        address:      form.value.address.trim()     || null,
        phone:        form.value.phone.trim()       || null,
        email:        form.value.email.trim()       || null,
        cover_image:  form.value.cover_image.trim() || null,
        description:  form.value.description.trim() || null,
        map_url:      form.value.map_url.trim()     || null,
        status:       form.value.status,
        category_ids: form.value.category_ids,
      }
      await axios.put(`/api/admin/booking/stores/${editId}`, payload)
      // 本地更新
      const idx    = stores.value.findIndex(s => s.id === editId)
      const region = regions.value.find(r => r.id === payload.region_id)
      if (idx !== -1) {
        stores.value[idx] = {
          ...stores.value[idx],
          ...payload,
          region_name: region?.name ?? stores.value[idx].region_name,
        }
      }
    } else {
      // 新增：POST，含 account + password
      const payload = {
        region_id:    Number(form.value.region_id),
        name:         form.value.name.trim(),
        address:      form.value.address.trim()     || null,
        phone:        form.value.phone.trim()       || null,
        email:        form.value.email.trim()       || null,
        cover_image:  form.value.cover_image.trim() || null,
        description:  form.value.description.trim() || null,
        map_url:      form.value.map_url.trim()     || null,
        category_ids: form.value.category_ids,
        account:      form.value.account.trim(),
        password:     form.value.password,
      }
      await axios.post('/api/admin/booking/stores', payload)
      await loadAll()   // 重新載入以取得 region_name 等 JOIN 欄位
    }
    closeModal()
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    apiError.value = e.response?.data?.message || '儲存失敗，請稍後再試'
  } finally {
    submitting.value = false
  }
}

// ── 下架（DELETE → 後端軟刪除 status=inactive）────────
function confirmDeactivate(store) {
  confirmTarget.value = store
  confirmError.value  = ''
  showConfirm.value   = true
}

async function doDeactivate() {
  confirmLoading.value = true
  confirmError.value   = ''
  try {
    await axios.delete(`/api/admin/booking/stores/${confirmTarget.value.id}`)
    const idx = stores.value.findIndex(s => s.id === confirmTarget.value.id)
    if (idx !== -1) stores.value[idx].status = 'inactive'
    showConfirm.value = false
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    confirmError.value = e.response?.data?.message || '操作失敗，請稍後再試'
  } finally {
    confirmLoading.value = false
  }
}

// ── 硬刪除 ────────────────────────────────────────────
const showDeleteConfirm = ref(false)
const deleteTarget      = ref(null)
const deleteLoading     = ref(false)
const deleteError       = ref('')

function confirmHardDelete(store) {
  deleteTarget.value  = store
  deleteError.value   = ''
  showDeleteConfirm.value = true
}

async function doHardDelete() {
  deleteLoading.value = true
  deleteError.value   = ''
  try {
    await axios.delete(`/api/admin/booking/stores/${deleteTarget.value.id}/hard`)
    stores.value = stores.value.filter(s => s.id !== deleteTarget.value.id)
    showDeleteConfirm.value = false
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    deleteError.value = e.response?.data?.message || '刪除失敗，請稍後再試'
  } finally {
    deleteLoading.value = false
  }
}

// ── 重新上線（PUT status=active）─────────────────────
async function reactivate(store) {
  reactivating[store.id] = true
  try {
    await axios.put(`/api/admin/booking/stores/${store.id}`, {
      region_id:   store.region_id,
      name:        store.name,
      address:     store.address     || null,
      phone:       store.phone       || null,
      email:       store.email       || null,
      cover_image: store.cover_image || null,
      description: store.description || null,
      status:      'active',
    })
    store.status = 'active'
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    alert(e.response?.data?.message || '操作失敗，請稍後再試')
  } finally {
    reactivating[store.id] = false
  }
}

function copyStoreApplyLink() {
  navigator.clipboard.writeText('https://jiliskin.com/store-apply').then(() => {
    copiedApply.value = true
    setTimeout(() => copiedApply.value = false, 2000)
  })
}

function copyStoreLoginLink() {
  navigator.clipboard.writeText('https://jiliskin.com/store-admin/login').then(() => {
    copiedLogin.value = true
    setTimeout(() => copiedLogin.value = false, 2000)
  })
}

onMounted(loadAll)
</script>

<style scoped>
.abs-page {
  padding: 24px;
  max-width: 960px;
}

/* 頁首 */
.abs-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 24px; gap: 12px; flex-wrap: wrap;
}
.abs-title { font-size: 1.4rem; font-weight: 700; color: #2c3e50; margin: 0; }

/* 篩選 */
.abs-filters {
  display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 24px;
}

/* 狀態佔位 */
.abs-state {
  display: flex; align-items: center; gap: 10px;
  color: #7f8c8d; padding: 40px 0; font-size: 0.95rem;
}
.abs-alert--error {
  background: #fdedec; border: 1px solid #f5b7b1; border-radius: 8px;
  padding: 12px 16px; color: #c0392b; font-size: 0.9rem; margin-bottom: 16px;
}
.abs-empty {
  text-align: center; color: #95a5a6; padding: 60px 0; font-size: 0.95rem;
}

/* 店家列表 */
.abs-list { display: flex; flex-direction: column; gap: 12px; }

.abs-card {
  display: flex;
  background: #fff; border: 1px solid #e8ecf0;
  border-radius: 10px; overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  transition: opacity 0.2s;
}
.abs-card--inactive { opacity: 0.55; }

/* 封面縮圖 */
.abs-card__thumb {
  width: 120px;
  height: 120px;   /* 1:1 正方形 */
  flex-shrink: 0;
  background: #f4f6f9;
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
}
.abs-card__img {
  width: 100%; height: 100%;
  object-fit: cover; object-position: center top; display: block;
  image-rendering: -webkit-optimize-contrast;
}
.abs-card__no-img  { font-size: 0.75rem; color: #bbb; }

/* 卡片主體 */
.abs-card__body {
  flex: 1; padding: 14px 16px;
  display: flex; flex-direction: column; gap: 6px; min-width: 0;
}
.abs-card__top {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.abs-store-name { font-size: 1rem; font-weight: 700; color: #2c3e50; }

.abs-status-tag {
  display: inline-block; padding: 2px 9px; border-radius: 20px;
  font-size: 0.75rem; font-weight: 600;
}
.abs-status-tag--active   { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
.abs-status-tag--inactive { background: #f2f3f4; color: #5d6d7e; border: 1px solid #d5d8dc; }
.abs-status-tag--pending  { background: #fef9e7; color: #9a7d0a; border: 1px solid #f9e79f; }

.abs-card__meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  font-size: 0.84rem; color: #555;
}
.abs-region-tag {
  background: #eaf0fb; color: #1a5276;
  padding: 1px 8px; border-radius: 4px;
  font-size: 0.78rem; font-weight: 500;
}
.abs-email { color: #7f8c8d; }

.abs-card__addr { font-size: 0.82rem; color: #7f8c8d; }
.abs-card__desc {
  font-size: 0.82rem; color: #888;
  display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden;
}

/* 操作欄 */
.abs-card__actions {
  display: flex; flex-direction: column; gap: 8px;
  padding: 14px 12px; justify-content: center; flex-shrink: 0;
}

/* 按鈕 */
.abs-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 16px; border-radius: 7px; border: none;
  font-size: 0.9rem; font-weight: 500; cursor: pointer;
  transition: background 0.15s, opacity 0.15s; white-space: nowrap;
}
.abs-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.abs-btn--sm        { padding: 6px 12px; font-size: 0.82rem; }
.abs-btn--primary   { background: #2c3e50; color: #fff; }
.abs-btn--primary:hover:not(:disabled)   { background: #34495e; }
.abs-btn--secondary { background: #fff; color: #2c3e50; border: 1px solid #dce1e7; }
.abs-btn--secondary:hover:not(:disabled) { background: #f4f6f9; }
.abs-btn--copy      { background: #8B4513; color: #fff; border-color: #8B4513; }
.abs-btn--copy:hover:not(:disabled)      { background: #6b3310; }
.abs-header-actions { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; }
.abs-btn--warn      { background: #e67e22; color: #fff; }
.abs-btn--warn:hover:not(:disabled)      { background: #ca6f1e; }
.abs-btn--success   { background: #27ae60; color: #fff; }
.abs-btn--success:hover:not(:disabled)   { background: #229954; }
.abs-btn--info      { background: #2980b9; color: #fff; }
.abs-btn--info:hover:not(:disabled)      { background: #2471a3; }
.abs-btn--danger    { background: #e74c3c; color: #fff; }
.abs-btn--danger:hover:not(:disabled)    { background: #c0392b; }
.abs-btn--featured-on  { background: #f39c12; color: #fff; }
.abs-btn--featured-on:hover:not(:disabled)  { background: #d68910; }
.abs-btn--featured-off { background: #fff; color: #888; border: 1px solid #dce1e7; }
.abs-btn--featured-off:hover:not(:disabled) { background: #fef9e7; color: #f39c12; border-color: #f39c12; }
.abs-featured-badge { font-size: 0.72rem; background: #fff3cd; color: #856404; border: 1px solid #ffc107; border-radius: 20px; padding: 2px 8px; font-weight: 600; margin-left: 4px; }
.abs-btn--link      { background: transparent; border: 1px solid #8e44ad; color: #8e44ad; }
.abs-btn--link:hover:not(:disabled)      { background: #f5eefa; }

/* Spinner */
.abs-spinner {
  display: inline-block; width: 28px; height: 28px;
  border: 3px solid rgba(0,0,0,0.12); border-top-color: #7f8c8d;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
.abs-spinner--sm    { width: 14px; height: 14px; border-width: 2px; }
.abs-spinner--lg    { width: 22px; height: 22px; }
.abs-spinner--white { border-color: rgba(255,255,255,0.3); border-top-color: #fff; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Modal */
.abs-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  z-index: 600; display: flex; align-items: flex-start;
  justify-content: center; padding: 16px; overflow-y: auto;
}
.abs-modal {
  background: #fff; border-radius: 14px; width: 100%; max-width: 560px;
  margin: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18);
}
.abs-modal--sm { max-width: 400px; }

.abs-modal__header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 24px 16px; border-bottom: 1px solid #e9ecef;
  position: sticky; top: 0; background: #fff; z-index: 1;
  border-radius: 14px 14px 0 0;
}
.abs-modal__title  { font-size: 1.1rem; font-weight: 600; color: #2c3e50; margin: 0; }
.abs-modal__close  {
  background: none; border: none; font-size: 1.1rem; cursor: pointer;
  color: #aaa; padding: 4px 8px; border-radius: 4px; line-height: 1;
}
.abs-modal__close:hover { background: #f4f6f9; color: #555; }

.abs-modal__body {
  padding: 20px 24px;
  display: flex; flex-direction: column; gap: 0;
}
.abs-modal__hint { font-size: 0.85rem; color: #7f8c8d; margin: 6px 0 0; }
.abs-modal__footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 24px 20px; border-top: 1px solid #e9ecef; flex-wrap: wrap;
}

/* 表單分區 */
.abs-section-label {
  font-size: 0.82rem; font-weight: 700; color: #2c3e50;
  text-transform: uppercase; letter-spacing: 0.04em;
  margin: 0 0 12px;
}
.abs-section-hint {
  font-weight: 400; font-size: 0.78rem; color: #7f8c8d;
  text-transform: none; letter-spacing: 0; margin-left: 8px;
}
.abs-divider {
  border: none; border-top: 1px dashed #e0e6ed;
  margin: 16px 0 16px;
}

/* 表單 */
.abs-field       { margin-bottom: 14px; }
.abs-field--no-mb { margin-bottom: 0; }
.abs-field label {
  display: block; font-size: 0.87rem; font-weight: 500;
  color: #555; margin-bottom: 5px;
}
.abs-field-row {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 14px; margin-bottom: 14px;
}
.abs-req { color: #e74c3c; }

.abs-input {
  width: 100%; padding: 9px 12px;
  border: 1px solid #dce1e7; border-radius: 8px;
  font-size: 0.92rem; background: #fff;
  box-sizing: border-box; transition: border-color 0.15s;
  -webkit-appearance: none;
}
.abs-input:focus {
  outline: none; border-color: #2c3e50;
  box-shadow: 0 0 0 3px rgba(44,62,80,0.07);
}
.abs-input--error  { border-color: #e74c3c; }
.abs-input--select { min-width: 130px; }
.abs-input--search { min-width: 160px; }
.abs-textarea      { resize: vertical; min-height: 72px; }

.abs-field-err  { font-size: 0.8rem; color: #e74c3c; margin: 3px 0 0; }
.abs-required   { color: #e74c3c; margin-left: 2px; }

/* 封面圖上傳列 */
.abs-img-actions {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.abs-img-or { color: #aaa; font-size: 0.85rem; flex-shrink: 0; }
.abs-img-url-input { flex: 1; min-width: 180px; }
.abs-img-upload-btn { cursor: pointer; flex-shrink: 0; }
.abs-img-uploading {
  display: flex; align-items: center; gap: 8px;
  margin-top: 8px; font-size: 0.85rem; color: #7f8c8d;
}
.abs-img-err {
  margin-top: 8px; font-size: 0.82rem; color: #e74c3c;
}
.abs-img-hint, .abs-field-hint {
  margin: 5px 0 0; font-size: 0.78rem; color: #aaa;
}

/* 封面預覽：3:4 直式（跟前台一致） */
.abs-preview-wrap { margin-top: 12px; display: flex; flex-direction: column; gap: 4px; }
.abs-preview-frame {
  position: relative;
  width: 160px;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid #dce1e7;
  background: #f4f6f9;
}
.abs-preview-img {
  width: 100%; height: 100%;
  object-fit: cover; object-position: center top; display: block;
}
.abs-preview-badge {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: rgba(0,0,0,0.62);
  color: #fff; font-size: 0.68rem;
  padding: 4px 6px; line-height: 1.5;
}
.abs-preview-warn { color: #f39c12; display: block; }
.abs-preview-hint { font-size: 0.78rem; color: #95a5a6; margin: 0; }

/* Radio */
.abs-radio-group { display: flex; gap: 20px; }
.abs-radio {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.9rem; cursor: pointer;
}

/* 服務分類標籤（卡片上） */
.abs-cat-tags {
  display: flex; flex-wrap: wrap; gap: 5px;
}
.abs-cat-tag {
  background: #fdf0e8; color: #8b5e3c;
  border: 1px solid #f5cba7; border-radius: 4px;
  padding: 1px 8px; font-size: 0.76rem; font-weight: 500;
}

/* 服務分類 Checkbox 群組（表單中） */
.abs-checkbox-group {
  display: flex; flex-wrap: wrap; gap: 10px 18px;
  padding: 10px 12px;
  border: 1px solid #dce1e7; border-radius: 8px;
  background: #fafbfc;
}
.abs-checkbox {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.9rem; cursor: pointer;
}
.abs-checkbox input[type="checkbox"] { cursor: pointer; }

/* API 錯誤 */
.abs-api-error {
  background: #fdedec; border: 1px solid #f5b7b1; border-radius: 7px;
  padding: 10px 14px; font-size: 0.87rem; color: #c0392b; margin-top: 8px;
}

/* RWD */
@media (max-width: 640px) {
  .abs-page          { padding: 14px; }
  .abs-card          { flex-direction: column; }
  .abs-card__thumb   { width: 100%; height: 240px; }
  .abs-card__actions { flex-direction: row; padding: 10px 14px 14px; }
  .abs-field-row     { grid-template-columns: 1fr; }
  .abs-modal__footer { flex-direction: column-reverse; }
  .abs-modal__footer .abs-btn { width: 100%; justify-content: center; }
}
</style>
